/**
 * Badge Component
 * Reusable badge for status indicators
 */

import React from 'react';

export type BadgeVariant = 
  | 'success' 
  | 'error' 
  | 'warning' 
  | 'info' 
  | 'default'
  | 'primary'
  | 'secondary';

export type BadgeSize = 'sm' | 'md' | 'lg';

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  className?: string;
}

const Badge: React.FC<BadgeProps> = ({ 
  children, 
  variant = 'default',
  size = 'md',
  className = '' 
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'info':
        return 'bg-blue-100 text-blue-800';
      case 'primary':
        return 'bg-green-600 text-white';
      case 'secondary':
        return 'bg-gray-600 text-white';
      case 'default':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return 'px-2 py-0.5 text-xs';
      case 'lg':
        return 'px-4 py-2 text-base';
      case 'md':
      default:
        return 'px-3 py-1 text-sm';
    }
  };

  return (
    <span
      className={`inline-flex items-center font-medium rounded-full ${getVariantStyles()} ${getSizeStyles()} ${className}`}
    >
      {children}
    </span>
  );
};

export default Badge;

// Specific badge variants for common use cases
export const SuccessBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Badge variant="success">{children}</Badge>
);

export const ErrorBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Badge variant="error">{children}</Badge>
);

export const WarningBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Badge variant="warning">{children}</Badge>
);

export const InfoBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Badge variant="info">{children}</Badge>
);

